/**
 * OfficerSelector (Phase-2)
 *
 * Shown on officer-scoped pages when no officer context is present (e.g. a
 * LokBhavan/Office user opened the page directly from the sidebar without
 * ?officerId=). Lets them pick an officer; `onSelect(id)` typically navigates
 * to `?officerId=<id>` so `useCurrentOfficer` resolves it and the page loads.
 *
 * Reuses the existing `fetchOfficers` thunk — no new API. Additive only.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useMemo } from 'react';
import { Alert, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOfficers } from '../../store/slices/officerSlice';
import { RootState, AppDispatch } from '../../store';

interface OfficerRow {
  id: number;
  fullName?: string;
  designation?: string;
  department?: string;
  cadreDisplayName?: string;
}

interface OfficerSelectorProps {
  onSelect: (officerId: number) => void;
  message?: string;
}

const OfficerSelector: React.FC<OfficerSelectorProps> = ({ onSelect, message }) => {
  const dispatch = useDispatch<AppDispatch>();
  const officersState = useSelector((s: RootState) => s.officer);

  useEffect(() => {
    const raw = officersState.officers as unknown;
    const has = Array.isArray(raw) ? (raw as unknown[]).length > 0 : !!((raw as any)?.content?.length);
    if (!has && !officersState.loading) dispatch(fetchOfficers() as any);
  }, [dispatch, officersState.officers, officersState.loading]);

  const officers: OfficerRow[] = useMemo(() => {
    const raw = officersState.officers as unknown;
    return Array.isArray(raw) ? (raw as OfficerRow[]) : ((raw as any)?.content as OfficerRow[]) || [];
  }, [officersState.officers]);

  return (
    <Alert severity="info" sx={{ mb: 2 }} icon={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>{message || 'Select an officer to manage their records on their behalf.'}</Box>
        <FormControl size="small" sx={{ minWidth: 380 }} disabled={officersState.loading && officers.length === 0}>
          <InputLabel>Select Officer</InputLabel>
          <Select
            label="Select Officer"
            defaultValue=""
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v > 0) onSelect(v);
            }}
          >
            <MenuItem value="" disabled>Choose an officer…</MenuItem>
            {officers.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.fullName || 'Officer'} — {o.designation || o.department || o.cadreDisplayName || 'Officer'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Alert>
  );
};

export default OfficerSelector;